/* eslint-disable no-nested-ternary */
import axios from 'axios'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { getSbpQRPaymentParamsByTspUrl, PaymentFormValues } from '@entities/payment'
import {
  checkIsServiceNonTopUp,
  checkIsGameKey,
  getVisibleTspFields,
  useCyberStore,
  PaymentPayee,
  checkIsWithPercentFee,
} from '@entities/tsp'
import { useToast } from '@shared/lib/toast'
import { MESSAGE_QR, MESSAGE_QR_DUBLICATE, MESSAGE_QR_STEAM, MESSAGE_QR_VPN } from '@shared/config'
import { generateUuid } from '@shared/lib'
import { Currency } from '@root/types/generated'
import { Partners, usePartner } from '@shared/lib/partner'
import { useBreakpoint } from '@shared/lib/react'
import { transformAmountForQR } from '../lib/transformAmountForQR'
import { useFetchQR } from './useFetchQR'
import { useFetchQRStatus } from './useFetchQRStatus'
import { getSbpRedirectUrl } from '../lib/getSbpRedirectUrl'

interface UsePayBySBPArgs {
  isVouchers: boolean
  payee: PaymentPayee
  id: string
  setId: Dispatch<SetStateAction<string>>
}

export const usePayBySBP = ({ isVouchers, payee, id, setId }: UsePayBySBPArgs) => {
  const [qrLink, setQrLink] = useState<string>('')
  const [qrcId, setQrcId] = useState<string>('')

  const router = useRouter()
  const { toast } = useToast()
  const { isMobile } = useBreakpoint()
  const { partner } = usePartner()

  const { amountInfo, isPromocodeActivated, activeDenomination, lastActivatedPromocode, setPaymentLoading } =
    useCyberStore()

  const isPaymentInProgressRef = useRef(false)

  const { fetchQR, isWebView } = useFetchQR(id)
  const { redirectToResultDesktopSBP } = useFetchQRStatus({ id, qrLink })

  const isFromPubgGiveawayPage = router.query.from === 'rozygrysh-prizov-pubg-mobile'
  const isSteam = payee.features?.tsp_url === 'steam'
  const isPartner = partner !== Partners.DEFAULT

  useEffect(() => {
    const url = router.query.id as string

    if (redirectToResultDesktopSBP) {
      const isNonTopUpService = checkIsServiceNonTopUp(isVouchers, payee)
      const isTopUpWithPercentFeeService = checkIsWithPercentFee(payee)

      router.push(
        getSbpRedirectUrl({
          id,
          url,
          isWebView,
          isFromPubgGiveawayPage,
          isNonTopUpService,
          isTopUpWithPercentFeeService,
        })
      )
    }
  }, [redirectToResultDesktopSBP, id, router])

  const getServiceId = () => {
    if (isSteam) {
      if (isPromocodeActivated) return '24916'

      if (!isVouchers) {
        if (isPartner) return '21976'

        return payee.serviceId
      }

      return activeDenomination?.serviceId
    }

    return activeDenomination?.serviceId
  }

  const payBySBP = (values: PaymentFormValues) => {
    if (isPaymentInProgressRef.current) return
    isPaymentInProgressRef.current = true
    setPaymentLoading(true)

    const paymentParams = getSbpQRPaymentParamsByTspUrl({
      values,
      lastActivatedPromocode,
      isVoucher: isVouchers,
      isGameKey: checkIsGameKey(payee),
      url: payee.features?.tsp_url ?? '',
      fields: getVisibleTspFields(payee),
    })

    const isSteam = payee.features?.tsp_url === 'steam'
    const isNonTopUpService = checkIsServiceNonTopUp(isVouchers, payee)
    const isTopUpWithPercentFeeService = checkIsWithPercentFee(payee)

    fetchQR({
      data: {
        serviceId: getServiceId(),
        redirectUrl: getSbpRedirectUrl({
          id,
          isWebView,
          isFromPubgGiveawayPage,
          partner,
          url: payee.features?.tsp_url,
          isNonTopUpService,
          isTopUpWithPercentFeeService,
        }),
        amount: {
          base: transformAmountForQR(amountInfo?.base as string),
          fee: isPromocodeActivated && isSteam ? 0 : transformAmountForQR(amountInfo?.fee || 0),
          total:
            isPromocodeActivated && isSteam
              ? transformAmountForQR(amountInfo?.base as string)
              : transformAmountForQR(amountInfo?.total as string),
          currency: Currency.RUB,
        },
        ...paymentParams,
      },
    })
      .then((res) => {
        if (res.data && res.data.payload) {
          if (!isMobile) {
            setQrcId(res.data.qrcId || '')
            setQrLink(res.data.payload)
          } else {
            setId(generateUuid())

            window.location.href = res.data.payload
          }
        }
      })
      .catch((res) => {
        if (axios.isCancel(res) || res?.code === 'ERR_CANCELED') return

        setId(generateUuid())

        if (res?.message === 'net::ERR_FAILED 403') {
          toast({ variant: 'error', description: MESSAGE_QR_VPN })

          return
        }

        if (res?.response?.data?.code === 2403) {
          toast({ variant: 'error', description: MESSAGE_QR_DUBLICATE })

          return
        }

        const isGenshinService = payee.features?.tsp_url === 'genshin'

        if (
          (res?.response?.data?.code === 2409 && !isVouchers) ||
          (isGenshinService && res?.response?.data?.code === 2409)
        ) {
          toast({ variant: 'error', description: MESSAGE_QR_STEAM })
        } else {
          toast({ variant: 'error', description: MESSAGE_QR })
        }
      })
      .finally(() => {
        setPaymentLoading(false)
        isPaymentInProgressRef.current = false
      })
  }

  const cancelSBPPayment = () => {
    setQrLink('')
    setQrcId('')
    setId(generateUuid())
  }

  return { qrLink, qrcId, payBySBP, cancelSBPPayment }
}
